import { Injectable } from '@angular/core';
import { Firestore, collectionData, collection, doc, deleteDoc, query, where, orderBy, limit as firestoreLimit, getDoc, getCountFromServer, docData } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { Ad } from '../models/ad.interface';
import { Image } from '../models/image.interface';
import { User } from '../models/user.interface';

@Injectable({
  providedIn: 'root',
})
export class AdService {
  constructor(private firestore: Firestore) {}

  getAdList(uid: string): Observable<Ad[]> {
    const adsRef = collection(this.firestore, 'ge_users', uid, 'ads');
    return collectionData(adsRef, { idField: 'id' }) as Observable<Ad[]>;
  }

  getAdDetail(uid: string, id: string): Observable<Ad> {
    const adDoc = doc(this.firestore, `/ge_users/${uid}/ads/${id}`);
    return docData(adDoc, { idField: 'id' }) as Observable<Ad>;
  }

  async deleteAd(uid: string, id: string): Promise<void> {
    const userAdRef = doc(this.firestore, '/ge_users/' + uid + '/ads/' + id);
    const globalAdRef = doc(this.firestore, '/ge_ads/' + id);
    await deleteDoc(userAdRef);
    return await deleteDoc(globalAdRef);
  }

  getAdGallery(uid: string, id: string): Observable<Image[]> {
    const imagesRef = collection(this.firestore, '/ge_users/' + uid + '/ads/' + id + '/images/');
    const q = query(
      imagesRef,
      orderBy('index', 'desc'),
      firestoreLimit(5)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Image[]>;
  }

  async countClassifiedAds(classification: string, country: string): Promise<number> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async newClassifiedAds(classification: string, country: string): Promise<number> {
    const month = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true),
      where('publish', '>=', month)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countGlobalAds(classification: string): Promise<number> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async newGlobalAds(classification: string): Promise<number> {
    const month = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true),
      where('publish', '>=', month)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countIndustryAds(grouping: string, country: string): Promise<number> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('grouping', 'array-contains', grouping),
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countCountryAds(country: string): Promise<number> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('location', '==', 'onsite'),
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countRemoteAds(): Promise<number> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('location', '==', 'remote'),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countAds(): Promise<number> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  getCountryAds(country: string, limitValue: number): Observable<Ad[]> {
    const adCollectionRef = collection(this.firestore, 'ge_ads');
    const q = query(adCollectionRef,
      where('country', '==', country),
      where('location', '==', 'onsite'),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  getLocalAds(location: string, country: string, limitValue: number): Observable<Ad[]> {
    const adCollectionRef = collection(this.firestore, 'ge_ads');
    const q = query(adCollectionRef,
      where('country', '==', country),
      where('cities', 'array-contains', location),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  getRegionAds(location: string, country: string, limitValue: number): Observable<Ad[]> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('country', '==', country),
      where('regions', 'array-contains', location),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  getClassifiedAds(classification: string, country: string, limitValue: number): Observable<Ad[]> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  getAllClassifiedAds(classification: string, limitValue: number): Observable<Ad[]> {
    const adCollectionRef = collection(this.firestore, 'ge_ads');
    const q = query(adCollectionRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  getIndustryAds(grouping: string, country: string, limitValue: number): Observable<Ad[]> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('grouping', 'array-contains', grouping),
      where('country', '==', country),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  getAllIndustryAds(grouping: string, limitValue: number): Observable<Ad[]> {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(
      adsRef,
      where('grouping', 'array-contains', grouping),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  getRemoteAds(limitValue: number) {
    const adCollectionRef = collection(this.firestore, 'ge_ads');
    const q = query(adCollectionRef,
      where('location', '==', 'remote'),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  ads() {
    const adCollectionRef = collection(this.firestore, 'ge_ads');
    const q = query(adCollectionRef,
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(10)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  getProfile(uid: string) {
    const userRef = doc(this.firestore, `ge_users/${uid}`);
    return getDoc(userRef);
  }

  adCard(id: string) {
    const adRef = doc(this.firestore, `ge_ads/${id}`);
    return getDoc(adRef);
  }

  adDetail(id: string): Observable<Ad> {
    const adDoc = doc(this.firestore, `ge_ads/${id}`);
    return docData(adDoc, { idField: 'id' }) as Observable<Ad>;
  }

  adDetails(slug: string) {
    const adsRef = collection(this.firestore, 'ge_ads');
    const q = query(adsRef, where('slug', '==', slug));
    return collectionData(q, { idField: 'id' }) as Observable<Ad[]>;
  }

  adHunters(id: string): Observable<User[]> {
    const huntersRef = collection(this.firestore, `ge_ads/${id}/hunters`);
    const q = query(
      huntersRef,
      orderBy('applied', 'desc'),
      firestoreLimit(10)
    );
    return collectionData(q, { idField: 'id' }) as Observable<User[]>;
  }
}
